import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import clx from "clsx";
import DebugUtils from "@/utils/debugUtils";

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholder?: string;
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  loading?: "lazy" | "eager";
  aspectRatio?: string;
  priority?: boolean;
  srcSet?: string;
  sizes?: string;
  lowQualitySrc?: string; // LQIP
  maxRetries?: number;
  retryBaseDelayMs?: number;
  cacheBustOnRetry?: boolean;
  debug?: boolean; // Control debug info visibility
}

const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  className = "",
  placeholder = "/img/noimg.jpg",
  onError,
  loading = "lazy",
  aspectRatio = "16/9",
  priority = false,
  srcSet,
  sizes,
  lowQualitySrc,
  maxRetries = 3,
  retryBaseDelayMs = 500,
  cacheBustOnRetry = false,
  debug = false,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [currentSrc, setCurrentSrc] = useState(priority ? src : "");
  const [retryCount, setRetryCount] = useState(0);
  const [showLQIP, setShowLQIP] = useState(!!lowQualitySrc);

  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const mountedRef = useRef(true);
  const componentId = useRef(
    `lazy-img-${Math.random().toString(36).substr(2, 9)}`,
  );

  // Track image with DebugUtils
  useEffect(() => {
    DebugUtils.trackImage({
      src,
      alt,
      componentId: componentId.current,
      state: "loading",
      retryCount: 0,
    });
  }, [src, alt]);

  // Enhanced console logging for debugging
  const logDebug = useCallback(
    (message: string, data?: any) => {
      if (process.env.NODE_ENV === "development") {
        console.group(`[LazyImage:${componentId.current}] ${message}`);
        console.log("Image src:", src);
        console.log("Alt text:", alt);
        console.log("Component state:", {
          isLoaded,
          hasError,
          isInView,
          currentSrc,
          retryCount,
          priority,
          mounted: mountedRef.current,
        });
        if (data) {
          console.log("Additional data:", data);
        }
        console.groupEnd();
      }
    },
    [src, alt, isLoaded, hasError, isInView, currentSrc, retryCount, priority],
  );

  const logError = useCallback(
    (message: string, error?: any) => {
      console.group(`[LazyImage:${componentId.current}] ERROR: ${message}`);
      console.error("Image src:", src);
      console.error("Alt text:", alt);
      console.error("Component state:", {
        isLoaded,
        hasError,
        isInView,
        currentSrc,
        retryCount,
        priority,
        mounted: mountedRef.current,
      });
      if (error) {
        console.error("Error details:", error);
      }
      console.groupEnd();
    },
    [src, alt, isLoaded, hasError, isInView, currentSrc, retryCount, priority],
  );

  // Log component initialization
  useEffect(() => {
    logDebug("Component initialized", {
      props: {
        src,
        alt,
        priority,
        loading,
        aspectRatio,
        maxRetries,
        retryBaseDelayMs,
        cacheBustOnRetry,
      },
    });
  }, []);

  // Cleanup observer with safety checks
  const cleanupObserver = useCallback(() => {
    if (observerRef.current) {
      try {
        logDebug("Cleaning up intersection observer");
        observerRef.current.disconnect();
      } catch (error) {
        logError("Error disconnecting observer", error);
      }
      observerRef.current = null;
    }
  }, [logDebug, logError]);

  // Setup intersection observer for lazy loading with improved error handling
  useEffect(() => {
    mountedRef.current = true;
    logDebug("Setting up intersection observer", { priority, isInView });

    if (priority || isInView) {
      logDebug("Skipping observer setup - priority or already in view");
      return;
    }

    let timeoutId: NodeJS.Timeout;

    try {
      const observer = new IntersectionObserver(
        (entries) => {
          // Only process if component is still mounted
          if (!mountedRef.current) {
            logDebug("Observer callback triggered but component unmounted");
            return;
          }

          entries.forEach((entry) => {
            logDebug("Intersection observer entry", {
              isIntersecting: entry.isIntersecting,
              intersectionRatio: entry.intersectionRatio,
              boundingClientRect: entry.boundingClientRect,
            });

            if (entry.isIntersecting && mountedRef.current) {
              logDebug("Image entered viewport - setting in view");
              setIsInView(true);
              setCurrentSrc(src);
              cleanupObserver();
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: "100px", // Increased for better preloading
        },
      );

      // Delayed observer setup to prevent race conditions
      timeoutId = setTimeout(() => {
        if (imgRef.current && mountedRef.current) {
          logDebug("Observing image element");
          observer.observe(imgRef.current);
          observerRef.current = observer;
        } else {
          logDebug("Cannot observe - image ref not available or unmounted");
        }
      }, 0);
    } catch (error) {
      logError("Error setting up intersection observer", error);
      // Fallback: load image immediately if observer fails
      setIsInView(true);
      setCurrentSrc(src);
    }

    return () => {
      mountedRef.current = false;
      if (timeoutId) clearTimeout(timeoutId);
      cleanupObserver();
      logDebug("Component cleanup completed");
    };
  }, [priority, isInView, src, cleanupObserver, logDebug, logError]);

  // Handle retry logic with mounted check
  const handleRetry = useCallback(() => {
    if (retryCount < maxRetries && mountedRef.current) {
      const delay = retryBaseDelayMs * Math.pow(2, retryCount); // Exponential backoff

      logDebug(`Scheduling retry ${retryCount + 1}/${maxRetries}`, {
        delay,
        cacheBustOnRetry,
      });

      // Track retry with DebugUtils
      DebugUtils.trackImage({
        src,
        componentId: componentId.current,
        state: "retry",
        retryCount: retryCount + 1,
      });

      setTimeout(() => {
        if (!mountedRef.current) {
          logDebug("Retry cancelled - component unmounted");
          return;
        }

        logDebug(`Executing retry ${retryCount + 1}/${maxRetries}`);
        setRetryCount((prev) => prev + 1);
        setHasError(false);
        setIsLoaded(false);

        // Add cache bust parameter if enabled
        if (cacheBustOnRetry) {
          try {
            const url = new URL(src, window.location.origin);
            url.searchParams.set("retry", retryCount.toString());
            url.searchParams.set("t", Date.now().toString());
            const newSrc = url.toString();
            logDebug("Retry with cache bust URL", { originalSrc: src, newSrc });
            setCurrentSrc(newSrc);
          } catch (error) {
            logError("Error creating retry URL", error);
            setCurrentSrc(src);
          }
        } else {
          // Force reload by briefly changing src
          logDebug("Retry by resetting src");
          setCurrentSrc("");
          setTimeout(() => {
            if (mountedRef.current) {
              logDebug("Setting retry src");
              setCurrentSrc(src);
            }
          }, 10);
        }
      }, delay);
    } else {
      logDebug("Retry not executed", {
        retryCount,
        maxRetries,
        mounted: mountedRef.current,
        reason:
          retryCount >= maxRetries
            ? "Max retries reached"
            : "Component unmounted",
      });
    }
  }, [
    retryCount,
    maxRetries,
    retryBaseDelayMs,
    cacheBustOnRetry,
    src,
    logDebug,
    logError,
  ]);

  // Handle successful image load with mounted check
  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      if (!mountedRef.current) {
        logDebug("Load event ignored - component unmounted");
        return;
      }

      const imgElement = e.currentTarget as HTMLImageElement;
      const loadTime = performance.now() - (window as any)._imageLoadStart || 0;

      logDebug("Image loaded successfully", {
        naturalWidth: imgElement.naturalWidth,
        naturalHeight: imgElement.naturalHeight,
        src: imgElement.src,
        currentSrc: imgElement.currentSrc,
        loadTime,
      });

      // Track successful load with DebugUtils
      DebugUtils.trackImage({
        src,
        componentId: componentId.current,
        state: "loaded",
        retryCount,
        loadTime,
        dimensions: {
          width: imgElement.naturalWidth,
          height: imgElement.naturalHeight,
        },
      });

      setIsLoaded(true);
      setShowLQIP(false);
      setHasError(false);
      setRetryCount(0);
    },
    [logDebug],
  );

  // Handle image error with improved error handling
  const handleError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      if (!mountedRef.current) {
        logDebug("Error event ignored - component unmounted");
        return;
      }

      const imgElement = e.currentTarget as HTMLImageElement;
      const errorMessage = e.nativeEvent
        ? (e.nativeEvent as any).message || "Image load failed"
        : "Unknown error";

      logError("Image failed to load", {
        src: imgElement.src,
        currentSrc: imgElement.currentSrc,
        retryAttempt: retryCount + 1,
        maxRetries,
        error: e.nativeEvent,
      });

      // Track error with DebugUtils
      DebugUtils.trackImage({
        src,
        componentId: componentId.current,
        state: "error",
        retryCount,
        errorMessage,
      });

      setHasError(true);
      setIsLoaded(false);

      // Call custom error handler if provided
      try {
        onError?.(e);
      } catch (error) {
        logError("Error in custom error handler", error);
      }

      // Auto retry if retries are available
      if (retryCount < maxRetries) {
        logDebug("Scheduling automatic retry");
        handleRetry();
      } else {
        logDebug("Max retries reached - will show placeholder");
      }
    },
    [onError, retryCount, maxRetries, handleRetry, logDebug, logError],
  );

  // Memoized image source calculations for better performance
  const imageProps = useMemo(() => {
    const getImageSrc = () => {
      if (!isInView && !priority) {
        logDebug("Image not in view and not priority - no src");
        return "";
      }
      if (hasError && retryCount >= maxRetries) {
        logDebug("Max retries reached - using placeholder");
        return placeholder;
      }
      const finalSrc = currentSrc || src;
      logDebug("Final image src determined", {
        finalSrc,
        currentSrc,
        originalSrc: src,
      });
      return finalSrc;
    };

    const getImageSrcSet = () => {
      if (!isInView && !priority) return "";
      if (hasError && retryCount >= maxRetries) return "";
      return srcSet;
    };

    const finalProps = {
      src: getImageSrc(),
      srcSet: getImageSrcSet(),
      alt,
      loading,
      className: clx(
        "w-full h-full object-cover transition-opacity duration-300",
        {
          "opacity-100": isLoaded && !hasError,
          "opacity-0": !isLoaded || hasError,
        },
      ),
      style: {
        display: !isInView && !priority ? "none" : "block",
      },
    };

    logDebug("Image props calculated", finalProps);
    return finalProps;
  }, [
    isInView,
    priority,
    hasError,
    retryCount,
    maxRetries,
    placeholder,
    currentSrc,
    src,
    srcSet,
    alt,
    loading,
    isLoaded,
    logDebug,
  ]);

  // Log state changes
  useEffect(() => {
    logDebug("State changed", {
      isLoaded,
      hasError,
      isInView,
      retryCount,
      currentSrc,
    });
  }, [isLoaded, hasError, isInView, retryCount, currentSrc, logDebug]);

  return (
    <div
      className={clx(
        "relative overflow-hidden bg-gray-100 dark:bg-gray-800",
        className,
      )}
      style={{ aspectRatio }}
      data-component-id={componentId.current}
      data-image-src={src}
      data-image-state={hasError ? "error" : isLoaded ? "loaded" : "loading"}
    >
      {/* Low Quality Image Placeholder (LQIP) */}
      {showLQIP && lowQualitySrc && isInView && (
        <img
          src={lowQualitySrc}
          alt=""
          className="absolute inset-0 w-full h-full object-cover filter blur-sm scale-110 transition-opacity duration-300"
          loading="eager"
          style={{ opacity: isLoaded ? 0 : 1 }}
          onLoad={() => logDebug("LQIP loaded")}
          onError={() => logDebug("LQIP failed to load")}
        />
      )}

      {/* Main Image */}
      <img
        ref={imgRef}
        {...imageProps}
        sizes={sizes}
        onLoad={(e) => {
          // Start timing for DebugUtils
          (window as any)._imageLoadStart = performance.now();
          handleLoad(e);
        }}
        onError={handleError}
        data-retry-count={retryCount}
        data-max-retries={maxRetries}
        data-component-id={componentId.current}
      />

      {/* Loading/Error State - only show when LQIP not available or errors occur */}
      {(!isLoaded || hasError) && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-gray-800">
          {hasError && retryCount >= maxRetries ? (
            // Final error state - show placeholder or fallback
            <div className="w-full h-full flex items-center justify-center bg-gray-200 dark:bg-gray-700">
              <div className="text-center text-gray-500 dark:text-gray-400">
                <svg className="w-16 h-16 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
                <p className="text-sm">Gambar tidak tersedia</p>
              </div>
            </div>
          ) : hasError ? (
            // Retrying state
            <div className="flex flex-col items-center space-y-2 text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-gray-600"></div>
            </div>
          ) : !lowQualitySrc ? (
            // Loading state - only when LQIP not available
            <div className="flex flex-col items-center space-y-2 text-gray-500 dark:text-gray-400">
              <div className="animate-pulse bg-gray-300 dark:bg-gray-600 rounded w-12 h-12"></div>
            </div>
          ) : null}
        </div>
      )}

      {/* Manual retry button for failed images */}
      {hasError && retryCount >= maxRetries && (
        <button
          onClick={() => {
            if (mountedRef.current) {
              logDebug("Manual retry triggered");

              // Track manual retry with DebugUtils
              DebugUtils.trackImage({
                src,
                componentId: componentId.current,
                state: "retry",
                retryCount: 0,
              });

              setRetryCount(0);
              setHasError(false);
              setIsLoaded(false);
              setCurrentSrc(src);
            }
          }}
          className="absolute top-2 right-2 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 p-1 rounded-full shadow-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Coba lagi memuat gambar"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      )}

      {/* Debug info in development */}
      {debug && process.env.NODE_ENV === "development" && (
        <div className="absolute bottom-0 left-0 bg-black bg-opacity-75 text-white text-xs p-1 max-w-full overflow-hidden">
          <div>ID: {componentId.current}</div>
          <div>
            State: {hasError ? "ERROR" : isLoaded ? "LOADED" : "LOADING"}
          </div>
          <div>
            Retry: {retryCount}/{maxRetries}
          </div>
          <div>InView: {isInView.toString()}</div>
          <div title={src}>
            Src: {src.length > 30 ? src.substring(0, 30) + "..." : src}
          </div>
        </div>
      )}
    </div>
  );
};

export default LazyImage;
